import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Eye, RefreshCw, Mail, Plus, Trash2, MapPin, Star, Zap, Church } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/venue-watch`;
const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  'Content-Type': 'application/json',
});

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const day = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';

const VenueWatchAdmin = () => {
  const [newEvents, setNewEvents] = useState({ items: [], count: 0, watching: 0, since: null });
  const [big, setBig] = useState({ items: [], count: 0 });
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [form, setForm] = useState({ pattern: '', label: '' });
  const [error, setError] = useState('');

  const getJson = async (path, fallback) => {
    const res = await fetch(`${API}${path}`, { headers: auth() });
    if (!res.ok) throw new Error(res.status === 401 ? 'Your admin session has expired — sign in again.' : `Could not load ${path} (${res.status})`);
    const data = await res.json();
    return { ...fallback, ...data };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, v, b] = await Promise.all([
        getJson('/new', { items: [], count: 0, watching: 0, since: null }),
        getJson('/venues', { items: [] }),
        getJson('/big-bookings?days=14', { items: [], count: 0, pending: 0 }),
      ]);
      setNewEvents(n);
      setVenues(v.items || []);
      setBig(b);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (label, path, options = {}) => {
    setBusy(label); setNote(''); setError('');
    try {
      const res = await fetch(`${API}${path}`, { headers: auth(), method: 'POST', ...options });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || `That did not work (${res.status})`);
        return;
      }
      const data = await res.json();
      if (label === 'sync') {
        setNote(data.ok ? `OneRen: ${data.created} new, ${data.updated} updated, ${data.sold_out} sold out` : 'OneRen sync failed');
      } else if (label === 'abbey') {
        setNote(data.ok ? `Abbey: ${data.created} new, ${data.updated} updated from ${data.parsed} listings` : `Abbey sync failed: ${data.error}`);
      } else if (label === 'digest') {
        setNote(data.sent ? `Morning round-up sent to ${data.to}` : `Not sent: ${data.skipped || data.error}`);
      } else if (label === 'bigalert') {
        setNote(data.sent ? `Big booking email sent to ${data.to} (${data.shows} show${data.shows === 1 ? '' : 's'})` : `Not sent: ${data.skipped || data.error}`);
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const addVenue = async (e) => {
    e.preventDefault();
    if (!form.pattern.trim()) return;
    await act('add', '/venues', { body: JSON.stringify(form) });
    setForm({ pattern: '', label: '' });
  };

  const removeVenue = async (pattern) => {
    setBusy(pattern);
    try {
      await fetch(`${API}/venues/${encodeURIComponent(pattern)}`, { method: 'DELETE', headers: auth() });
      await load();
    } finally {
      setBusy('');
    }
  };

  const togglePriority = async (v) => {
    setBusy(v.pattern);
    try {
      await fetch(`${API}/venues/${encodeURIComponent(v.pattern)}/priority`, {
        method: 'PATCH', headers: auth(), body: JSON.stringify({ priority: !v.priority }),
      });
      await load();
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="venue-watch-admin">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Venue watch</h1>
            <p className="mt-2 text-base text-slate-600 max-w-xl">
              New gigs at the {newEvents.watching} venues you follow. Everything here is already live
              on the calendar — this is so you can cover the big bookings early.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={!!busy}
                    onClick={() => act('sync', '/sync-oneren')} data-testid="venue-sync-oneren">
              <RefreshCw size={14} className="mr-2" /> {busy === 'sync' ? 'Syncing...' : 'Sync OneRen'}
            </Button>
            <Button variant="outline" size="sm" disabled={!!busy}
                    onClick={() => act('abbey', '/sync-abbey')} data-testid="venue-sync-abbey">
              <Church size={14} className="mr-2" /> {busy === 'abbey' ? 'Syncing...' : 'Sync Abbey'}
            </Button>
            <Button variant="outline" size="sm" disabled={!!busy}
                    onClick={() => act('digest', '/digest')} data-testid="venue-send-digest">
              <Mail size={14} className="mr-2" /> {busy === 'digest' ? 'Sending...' : 'Email me this'}
            </Button>
          </div>
        </div>
        {note && <p className="mb-4 text-sm text-cyan-700" data-testid="venue-watch-note">{note}</p>}
        {error && (
          <p className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
             data-testid="venue-watch-error">
            {error}
          </p>
        )}

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 size={18} className="animate-spin inline" /></div>
        ) : (
          <>
            {/* Big bookings — the ones worth a story today */}
            <div className="rounded-xl border border-orange-200 bg-orange-50/60 overflow-hidden mb-8"
                 data-testid="big-bookings-panel">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-orange-100">
                <p className="text-sm font-semibold text-orange-900 flex items-center gap-2">
                  <Zap size={14} /> Big bookings · last 14 days
                  {big.pending > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-orange-600 px-1.5 py-0.5 rounded">
                      {big.pending} not emailed yet
                    </span>
                  )}
                </p>
                <Button size="sm" variant="ghost" disabled={!!busy}
                        className="text-orange-800 hover:bg-orange-100"
                        onClick={() => act('bigalert', '/big-alert?force=true')}
                        data-testid="venue-big-alert">
                  <Mail size={14} className="mr-2" /> {busy === 'bigalert' ? 'Sending...' : 'Email these to me'}
                </Button>
              </div>
              {(big.items || []).length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-orange-800/80" data-testid="big-bookings-empty">
                  No big bookings in the last fortnight. You will get an email within the hour
                  when a show goes on sale at a starred venue.
                </p>
              ) : (
                <ul className="divide-y divide-orange-100" data-testid="big-bookings-list">
                  {big.items.map((s) => (
                    <li key={s.key} className="px-4 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <a href={`/events/${s.slug}`} target="_blank" rel="noopener noreferrer"
                         className="font-semibold text-slate-900 hover:text-orange-700">
                        {s.title}
                      </a>
                      {!s.alerted && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                          New
                        </span>
                      )}
                      <span className="text-xs text-slate-600 flex items-center gap-1">
                        <MapPin size={11} /> {s.venue}
                      </span>
                      <span className="text-xs text-slate-600">
                        {(s.dates || []).map(day).join(', ')}
                        {s.performances > 1 && ` · ${s.performances} nights`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-8">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900">
                  {newEvents.count} new since {when(newEvents.since)}
                </p>
                {newEvents.count > 0 && (
                  <Button size="sm" variant="ghost" disabled={!!busy}
                          onClick={() => act('seen', '/seen')} data-testid="venue-mark-seen">
                    <Eye size={14} className="mr-2" /> Mark all as seen
                  </Button>
                )}
              </div>
              {(newEvents.items || []).length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="venue-watch-empty">
                  Nothing new since your last look. You will get a morning email when something appears.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="venue-watch-new-list">
                  {newEvents.items.map((e) => (
                    <li key={e.id} className="px-4 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <a href={`/events/${e.slug}`} target="_blank" rel="noopener noreferrer"
                         className="font-semibold text-slate-900 hover:text-cyan-700">
                        {e.title}
                      </a>
                      {e.big && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded"
                              data-testid="new-event-big-badge">
                          Big booking
                        </span>
                      )}
                      {e.sold_out && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                          Sold out
                        </span>
                      )}
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin size={11} /> {e.venue}
                      </span>
                      <span className="text-xs text-slate-500">{when(e.start_date)}</span>
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        {e.source}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <h2 className="text-lg font-bold text-slate-900 mb-1">Venues you follow</h2>
            <p className="text-sm text-slate-500 mb-3">
              Starred venues send an instant big-booking email. The rest wait for the morning round-up.
            </p>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 mb-4"
                 data-testid="venue-watch-list">
              {venues.map((v) => (
                <div key={v.pattern} className="px-4 py-3 flex items-center gap-3">
                  <button type="button" onClick={() => togglePriority(v)} disabled={busy === v.pattern}
                          className={`transition-colors ${v.priority ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-400'}`}
                          data-testid={`venue-priority-${v.pattern.replace(/\s+/g, '-')}`}
                          aria-label={`${v.priority ? 'Stop' : 'Start'} instant alerts for ${v.label}`}
                          title={v.priority ? 'Instant big-booking alerts on' : 'Instant big-booking alerts off'}>
                    <Star size={16} fill={v.priority ? 'currentColor' : 'none'} />
                  </button>
                  <span className="font-medium text-slate-900">{v.label}</span>
                  <span className="text-xs text-slate-500">{v.upcoming} upcoming</span>
                  <button type="button" onClick={() => removeVenue(v.pattern)} disabled={busy === v.pattern}
                          className="ml-auto text-slate-400 hover:text-rose-600 transition-colors"
                          data-testid={`venue-remove-${v.pattern.replace(/\s+/g, '-')}`}
                          aria-label={`Stop watching ${v.label}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>

            <form onSubmit={addVenue} className="flex flex-wrap gap-2" data-testid="venue-add-form">
              <Input value={form.pattern} onChange={(e) => setForm((f) => ({ ...f, pattern: e.target.value }))}
                     placeholder="Venue name to match, e.g. wynd centre"
                     className="max-w-xs" data-testid="venue-add-pattern" />
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                     placeholder="Display name (optional)"
                     className="max-w-xs" data-testid="venue-add-label" />
              <Button type="submit" disabled={!!busy} data-testid="venue-add-submit">
                <Plus size={14} className="mr-2" /> Watch venue
              </Button>
            </form>
            <p className="mt-2 text-xs text-slate-500">
              Matching is on the venue name, so &quot;bungalow&quot; catches &quot;The Bungalow Bar&quot;.
            </p>
          </>
        )}
    </div>
  );
};

export default VenueWatchAdmin;
